import React, { useEffect, useRef, useState } from 'react';
import { View, Text } from 'react-native';
import { useSubscription } from '../context/SubscriptionContext';
import {
  getAdsenseBannerProps,
  loadAdsenseScript,
  requestAdsenseFill,
} from '../utils/adsense';
import { createThemedStyles } from '../theme/useThemedStyles';

interface AdBannerProps {
  style?: object;
}

const AdBanner: React.FC<AdBannerProps> = ({ style }) => {
  const styles = useAdBannerStyles();
  const { shouldShowAds } = useSubscription();
  const config = getAdsenseBannerProps();
  const [scriptReady, setScriptReady] = useState(false);
  const filledRef = useRef(false);

  const showAds = shouldShowAds();

  useEffect(() => {
    if (!showAds || !config.enabled) {
      return;
    }

    let cancelled = false;
    loadAdsenseScript(config.clientId)
      .then(() => {
        if (!cancelled) {
          setScriptReady(true);
        }
      })
      .catch((error) => {
        console.error('AdSense: script load error', error);
      });

    return () => {
      cancelled = true;
    };
  }, [showAds, config.enabled, config.clientId]);

  useEffect(() => {
    if (!scriptReady || filledRef.current) {
      return;
    }
    filledRef.current = true;
    requestAdsenseFill();
  }, [scriptReady]);

  if (!showAds) {
    return null;
  }

  if (!config.enabled) {
    if (__DEV__) {
      return (
        <View style={[styles.container, styles.devPlaceholder, style]}>
          <Text style={styles.devText}>
            Web ads: set EXPO_PUBLIC_ADSENSE_CLIENT_ID and EXPO_PUBLIC_ADSENSE_BANNER_SLOT
          </Text>
        </View>
      );
    }
    return null;
  }

  return (
    <View style={[styles.container, style]}>
      {scriptReady
        ? React.createElement('ins', {
            className: 'adsbygoogle',
            style: {
              display: 'block',
              width: '100%',
              minHeight: 90,
              textAlign: 'center',
            },
            'data-ad-client': config.clientId,
            'data-ad-slot': config.bannerSlot,
            'data-ad-format': 'auto',
            'data-full-width-responsive': 'true',
          })
        : null}
    </View>
  );
};

const useAdBannerStyles = createThemedStyles((colors) => ({
  container: {
    backgroundColor: colors.grey[100],
    borderTopWidth: 1,
    borderTopColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 50,
    width: '100%',
  },
  devPlaceholder: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  devText: {
    fontSize: 12,
    color: colors.text.secondary,
    textAlign: 'center',
  },
}));

export default AdBanner;
